import React, { useRef } from 'react';
import { motion, useReducedMotion, useScroll, useSpring } from 'framer-motion';
import { Instagram, BarChart3, Users, TrendingUp } from 'lucide-react';
import { EASE_OUT } from '../ui/premiumKit';
import { IG_GRADIENT, accent, gradientText, tint } from './brandAccent';

// 첫 화면 '이용 방법' — 2026-11-03 디자인만 새로(대표 "이 컴포넌트는 지금 디자인 룰을 따른 것 같지 않다").
// 예전: 보라 유리 카드 넷이 겹쳐 자동으로 넘어가던 카드 묶음(ui/CardSwap의 InstagramAnalysisCardSwap).
// 지금: 번호 단계 카드 넷. 휴대폰은 좌우로 넘기고 위 진행 띠가 넘긴 만큼 차오른다. PC는 네 칸 + 단계를 잇는 선이 그려지며 나타난다.
// 글귀는 예전 카드 글귀 그대로(ui/CardSwap.tsx 267~294줄에서 옮김).
const STEPS = [
    {
        step: '01',
        title: 'Instagram 아이디 입력',
        description: '분석하고 싶은 Instagram 계정의 아이디를 입력하세요. 공개 계정만 분석 가능합니다.',
        icon: Instagram,
    },
    {
        step: '02',
        title: '실시간 데이터 수집',
        description: 'AI가 실시간으로 프로필 정보, 팔로워 데이터, 게시물 정보를 안전하게 수집합니다.',
        icon: BarChart3,
    },
    {
        step: '03',
        title: '방문자 패턴 분석',
        description: '수집된 데이터를 바탕으로 방문자의 관심사, 활동 패턴, 인구통계를 분석합니다.',
        icon: Users,
    },
    {
        step: '04',
        title: '상세 분석 결과',
        description: '시각화된 차트와 인사이트로 방문자 프로필과 마케팅 전략을 제공합니다.',
        icon: TrendingUp,
    },
] as const;

const StepCard: React.FC<{ s: (typeof STEPS)[number]; i: number }> = ({ s, i }) => {
    const reduce = useReducedMotion();
    const Icon = s.icon;
    return (
        <motion.div
            initial={reduce ? false : { opacity: 0, y: 18 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.35 }}
            transition={{ duration: 0.6, delay: i * 0.08, ease: EASE_OUT }}
            className="relative h-full rounded-[24px] border border-white/[0.08] bg-[#0F0F11] p-6"
        >
            <div className="flex items-center justify-between">
                <span className="text-[40px] font-bold leading-none" style={{ ...gradientText, fontVariantNumeric: 'tabular-nums', letterSpacing: '-0.04em' }}>
                    {s.step}
                </span>
                <span className="flex h-11 w-11 items-center justify-center rounded-full" style={{ background: tint(accent(i)), color: accent(i) }}>
                    <Icon className="h-5 w-5" />
                </span>
            </div>
            <h3 className="mt-8 text-[18px] font-semibold text-[#FAFAFA] break-keep">{s.title}</h3>
            <p className="mt-2 text-[14px] leading-relaxed text-[#A1A1AA] break-keep">{s.description}</p>
        </motion.div>
    );
};

export const HowItWorksSteps: React.FC = () => {
    const reduce = useReducedMotion();
    const rowRef = useRef<HTMLDivElement>(null);
    const { scrollXProgress } = useScroll({ container: rowRef });
    const fill = useSpring(scrollXProgress, { stiffness: 220, damping: 30, mass: 0.4 });

    return (
        <div>
            {/* 휴대폰: 좌우로 넘기기 + 넘긴 만큼 차오르는 진행 띠 */}
            <div className="lg:hidden">
                <div className="mb-5 h-[2px] w-full overflow-hidden rounded-full bg-white/[0.08]" aria-hidden>
                    <motion.div className="h-full origin-left rounded-full" style={{ scaleX: reduce ? 1 : fill, backgroundImage: IG_GRADIENT }} />
                </div>
                <div
                    ref={rowRef}
                    className="-mx-5 flex snap-x snap-mandatory gap-3 overflow-x-auto px-5 pb-2 sm:-mx-6 sm:px-6 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden"
                    style={{ scrollPaddingLeft: 20 }}
                >
                    {STEPS.map((s, i) => (
                        <div key={s.step} className="w-[78%] shrink-0 snap-start sm:w-[46%]">
                            <StepCard s={s} i={i} />
                        </div>
                    ))}
                    <div className="w-2 shrink-0" aria-hidden />
                </div>
            </div>

            {/* PC: 네 칸 + 단계를 잇는 선이 왼쪽에서 오른쪽으로 그려진다 */}
            <div className="hidden lg:block">
                <div className="relative mb-6 h-3" aria-hidden>
                    <div className="absolute left-0 right-0 top-1/2 h-px -translate-y-1/2 bg-white/[0.08]" />
                    <motion.div
                        className="absolute left-0 right-0 top-1/2 h-px origin-left -translate-y-1/2"
                        style={{ backgroundImage: IG_GRADIENT }}
                        initial={reduce ? false : { scaleX: 0 }}
                        whileInView={{ scaleX: 1 }}
                        viewport={{ once: true, amount: 0.6 }}
                        transition={{ duration: 1.4, ease: EASE_OUT }}
                    />
                    <div className="absolute inset-0 grid grid-cols-4 gap-5">
                        {STEPS.map((s, i) => (
                            <motion.span
                                key={s.step}
                                className="mt-0 h-3 w-3 rounded-full border-2 bg-black"
                                style={{ borderColor: accent(i) }}
                                initial={reduce ? false : { scale: 0 }}
                                whileInView={{ scale: 1 }}
                                viewport={{ once: true, amount: 0.6 }}
                                transition={{ duration: 0.4, delay: 0.25 + i * 0.3, ease: EASE_OUT }}
                            />
                        ))}
                    </div>
                </div>
                <div className="grid grid-cols-4 gap-5">
                    {STEPS.map((s, i) => (
                        <StepCard key={s.step} s={s} i={i} />
                    ))}
                </div>
            </div>
        </div>
    );
};

export default HowItWorksSteps;
